// src/pages/warehouse/components/ZeilenMenge.tsx
// Modul 26 B: Kartonmenge direkt an einer Zeile der Kommissionier-Liste stellen.
//
// Gestaltung: die Zeile bleibt, wie sie ist. Der Steller sitzt in der vorhandenen
// Aktionsspalte neben „Stornieren", in derselben Outline-Sprache — und der
// Bestaetigen-Knopf ERSCHEINT ERST, wenn die eingestellte Zahl von der gespeicherten
// abweicht. Bei 100+ Zeilen ist das der Unterschied zwischen einer Liste und einem
// Formular: ruhig im Normalfall, deutlich genau dort, wo jemand etwas geaendert hat.
//
// Fachlich der wichtige Punkt (siehe lib/mengen-plan.ts): die Zahl an der Zeile ist
// die offene POSITION, der Endpunkt kennt die GESAMTMENGE der Anforderung. Beides
// faellt auseinander, sobald ein Teil schon geholt wurde.
import { useRef, useState } from 'react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { useAendereZeilenmenge } from '@/api/warehouse'
import {
  klemmeKartons, deuteErgebnis, KARTONS_MIN, KARTONS_MAX,
  type Meldung, type Vorgangsspeicher,
} from '@/lib/fillup-anforderung'

export function ZeilenMenge({ merchantSku, menge, requestDbId, vorgaenge }: {
  merchantSku: string
  menge: number
  /** OI-134: die Anforderung, die diese Zeile zeigt. Geht mit auf die Leitung, damit
   *  der Server merkt, wenn die Anzeige inzwischen veraltet ist (Storno + Neuanlage
   *  derselben Sternnummer) — sonst landet die Mengenaenderung auf der Nachfolge-
   *  Anforderung. Die Nachbar-Schaltflaeche „Stornieren" nutzt dieselbe Id. */
  requestDbId: number
  /** Idempotenz-Speicher der SEITE, nicht der Zeile. Er muss ein Aus- und
   *  Wiedereinblenden der Zeile ueberleben (Suchfilter, Reiterwechsel) — sonst
   *  bekaeme die Wiederholung nach einem unklaren Ausgang eine neue Id und buchte
   *  ein zweites Mal (Codex-Review 2026-08-04, Befund 3). */
  vorgaenge: Vorgangsspeicher
}) {
  const [entwurfText, setEntwurfText] = useState(String(menge))
  const [meldung, setMeldung] = useState<Meldung | null>(null)
  /** Zuletzt erfolgreich gesendete Zielmenge. Solange die Liste noch nachlaedt,
   *  zeigt `menge` den alten Stand — ohne diesen Merker bliebe der Knopf stehen und
   *  ein zweiter Klick wuerde dieselbe Erhoehung ein zweites Mal buchen. */
  const [bestaetigt, setBestaetigt] = useState<number | null>(null)
  // Kommt aus den Daten eine ANDERE Menge (jemand anderes hat geaendert, die Liste
  // hat aufgefrischt), zieht der Steller nach. Ohne das zeigte die Zeile weiter die
  // alte Zahl und der Uebernehmen-Knopf haette die fremde Aenderung zurueckgedreht —
  // dieselbe Klasse „Anzeige laeuft dem Stand davon", die dieses Modul im Backend
  // repariert. React-Muster fuer „Zustand an eine geaenderte Eigenschaft anpassen":
  // Aenderung waehrend des Renderns statt in einem Effekt.
  const [letzteMenge, setLetzteMenge] = useState(menge)
  if (menge !== letzteMenge) {
    setLetzteMenge(menge)
    setEntwurfText(String(menge))
    setMeldung(null)
    setBestaetigt(null)
  }
  const sendenRef = useRef(false)
  const aendern = useAendereZeilenmenge()

  const entwurf = klemmeKartons(Number(entwurfText))
  const geaendert = entwurf !== menge && entwurf !== bestaetigt

  function stelle(delta: number) {
    setEntwurfText(String(klemmeKartons(entwurf + delta)))
  }

  async function uebernehmen() {
    if (sendenRef.current || !geaendert) return
    sendenRef.current = true
    try {
      // Idempotenz: die Id entsteht im Hook, sobald feststeht, WAS gesendet wird —
      // Doppelklick und Wiederholung desselben Schreibvorgangs tragen dieselbe.
      const ergebnis = await aendern.mutateAsync({
        merchantSku, alteMenge: menge, neueMenge: entwurf, requestDbId, vorgaenge,
      })
      if ('unveraendert' in ergebnis) return
      const neueMeldung = deuteErgebnis(ergebnis, entwurf)
      setMeldung(neueMeldung)
      if (neueMeldung.art === 'fehler') { toast.error(neueMeldung.text); return }
      toast.success(neueMeldung.text)
      setBestaetigt(entwurf)
    } catch (err) {
      // Ausgang unklar — Vorgang und Id bleiben stehen, die Wiederholung sendet
      // dieselbe Id und kann nichts doppelt buchen.
      const text = err instanceof Error ? err.message : 'Menge konnte nicht geändert werden'
      setMeldung({ art: 'fehler', text })
      toast.error(text)
    } finally {
      sendenRef.current = false
    }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-1">
        <Button
          type="button" size="icon" variant="outline" aria-label={`Ein Karton weniger für ${merchantSku}`}
          disabled={aendern.isPending || entwurf <= KARTONS_MIN}
          onClick={() => stelle(-1)}
        >
          −
        </Button>
        <Input
          aria-label={`Kartons für ${merchantSku}`}
          className="h-8 w-14 text-center text-base font-semibold tabular-nums"
          value={entwurfText}
          inputMode="numeric"
          disabled={aendern.isPending}
          onChange={(e) => setEntwurfText(e.target.value)}
          onBlur={() => setEntwurfText(String(entwurf))}
        />
        <Button
          type="button" size="icon" variant="outline" aria-label={`Ein Karton mehr für ${merchantSku}`}
          disabled={aendern.isPending || entwurf >= KARTONS_MAX}
          onClick={() => stelle(1)}
        >
          +
        </Button>
      </div>
      <span className="text-xs text-muted">Kartons</span>

      {geaendert && (
        <Button size="sm" disabled={aendern.isPending} onClick={() => void uebernehmen()}>
          {aendern.isPending ? 'Wird geändert…' : `Auf ${entwurf} ändern`}
        </Button>
      )}

      {meldung && (
        <span
          role="status"
          className={`text-xs ${
            meldung.art === 'fehler'
              ? 'text-red-600'
              : meldung.art === 'hinweis'
                ? 'text-amber-700'
                : 'text-emerald-700'
          }`}
        >
          {meldung.text}
        </span>
      )}
    </div>
  )
}
